import { parse, isValid, isAfter, addDays, getDay, format } from "date-fns";
import {
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LeagueScoring,
} from "../../enums";
import type { SeasonRoundItem } from "../leagueFormSchema";
import { FRONT_9_HOLES } from "./roundMapping";
import { settingsForRound } from "./roundConditions";

/**
 * The Round Plan is the set of rounds the system derives from the operator's
 * Season Setup answers. In 1.0 it is generated client-side (see
 * `generateRoundPlan`); a backend "round plan" endpoint will replace the
 * generator behind `useRoundPlan` without changing this shape or its consumers.
 */
export interface RoundPlanCourse {
  id: number;
  name: string;
}

export interface RoundPlanRound {
  roundNumber: number;
  courseId: number;
  courseName: string;
  // Fixed-time rounds carry a 7-day window; Flex rounds carry only a due-by
  // date (no window), so these are optional.
  playWindowStartUtc?: string;
  playWindowEndUtc?: string;
  dueByUtc?: string;
  settings?: SeasonRoundItem["settings"];
}

export interface RoundPlan {
  scheduleMode: Clubhouse_LeagueWizardScheduleMode;
  rounds: RoundPlanRound[];
}

export interface GenerateRoundPlanInput {
  seasonStart: string; // yyyy-MM-dd
  seasonEnd: string; // yyyy-MM-dd
  scheduleMode: Clubhouse_LeagueWizardScheduleMode;
  // Fixed-time only: 0=Sun … 6=Sat, the weekday each weekly round opens on.
  weekDay?: number;
  // Flex only: how many rounds to pre-create (1–52).
  flexRoundCount?: number;
  // Courses to assign round-robin (the real API assigns these server-side).
  courses?: RoundPlanCourse[];
  settings?: SeasonRoundItem["settings"];
  // "Randomize settings for me": layer per-round conditions over `settings`,
  // leaving its handicap and automation alone. Deterministic by round number —
  // see roundConditions.ts.
  varyConditions?: boolean;
}

const ISO = "yyyy-MM-dd";

function toDate(iso: string): Date | undefined {
  const d = parse(iso, ISO, new Date());
  return isValid(d) ? d : undefined;
}

/** Round-robin course assignment; empty when no courses are available. */
function courseAt(courses: RoundPlanCourse[], index: number): RoundPlanCourse {
  if (courses.length === 0) return { id: 0, name: "" };
  return courses[index % courses.length]!;
}

/**
 * Client-side stand-in for the round-plan API.
 *
 * - **Fixed-time (WeeklyRounds):** one round per week, opening on `weekDay` and
 *   closing the night before the next round opens (a full, non-overlapping
 *   7-day window), for every week that starts within the season.
 * - **Flex (SeasonFlex):** `flexRoundCount` rounds with no play window — each
 *   carries a single due-by date (the season end).
 */
export function generateRoundPlan(input: GenerateRoundPlanInput): RoundPlan {
  const {
    scheduleMode,
    courses = [],
    settings,
    varyConditions = false,
  } = input;
  const settingsFor = (roundNumber: number) =>
    withDefaultHoles(settingsForRound(roundNumber, settings, varyConditions));
  const start = toDate(input.seasonStart);
  const end = toDate(input.seasonEnd);
  const rounds: RoundPlanRound[] = [];

  if (!start || !end || isAfter(start, end)) {
    return { scheduleMode, rounds };
  }

  if (scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex) {
    const count = Math.min(Math.max(input.flexRoundCount ?? 0, 0), 52);
    for (let i = 0; i < count; i += 1) {
      const c = courseAt(courses, i);
      rounds.push({
        roundNumber: i + 1,
        courseId: c.id,
        courseName: c.name,
        dueByUtc: input.seasonEnd,
        settings: settingsFor(i + 1),
      });
    }
    return { scheduleMode, rounds };
  }

  // Fixed-time weekly: walk to the first `weekDay` on/after the season start,
  // then step a week at a time until we pass the season end.
  const weekDay = input.weekDay;
  if (weekDay == null) return { scheduleMode, rounds };

  let open = start;
  while (getDay(open) !== weekDay) open = addDays(open, 1);

  let i = 0;
  while (!isAfter(open, end)) {
    const c = courseAt(courses, i);
    // Closes the night before the next round opens (a full 7-day window), but
    // never past the season end — the final week is clamped so no round window
    // spills beyond the league's end date.
    const fullClose = addDays(open, 6);
    const close = isAfter(fullClose, end) ? end : fullClose;
    rounds.push({
      roundNumber: i + 1,
      courseId: c.id,
      courseName: c.name,
      playWindowStartUtc: format(open, ISO),
      playWindowEndUtc: format(close, ISO),
      settings: settingsFor(i + 1),
    });
    open = addDays(open, 7);
    i += 1;
  }

  return { scheduleMode, rounds };
}

/**
 * The play window the weekly schedule already assigns to `roundNumber`.
 * Operators don't pick or edit this — Add Round uses it when that week still
 * falls inside the season.
 */
export function weeklyPlayWindowForRound(input: {
  seasonStart: string;
  seasonEnd: string;
  weekDay: number;
  roundNumber: number;
}): { start: string; end: string } | undefined {
  const planned = generateRoundPlan({
    seasonStart: input.seasonStart,
    seasonEnd: input.seasonEnd,
    scheduleMode: Clubhouse_LeagueWizardScheduleMode.WeeklyRounds,
    weekDay: input.weekDay,
  }).rounds.find((round) => round.roundNumber === input.roundNumber);
  if (!planned?.playWindowStartUtc || !planned?.playWindowEndUtc) {
    return undefined;
  }
  return {
    start: planned.playWindowStartUtc,
    end: planned.playWindowEndUtc,
  };
}

/** Full 7-day weekly window immediately after a prior round's facility-local close. */
export function weeklyPlayWindowAfterPreviousRoundEnd(
  previousRoundEndLocal: string,
): { start: string; end: string } | undefined {
  const lastEnd = toDate(previousRoundEndLocal);
  if (!lastEnd) return undefined;
  const nextStart = addDays(lastEnd, 1);
  const nextEnd = addDays(nextStart, 6);
  return {
    start: format(nextStart, ISO),
    end: format(nextEnd, ISO),
  };
}

/**
 * Play window for appending a weekly fixed round while Draft. Uses the in-season
 * planner when that round still fits; otherwise continues the cadence from the
 * last saved round (Draft append — season end may still match the prior final round).
 */
export function weeklyPlayWindowForNewRound(input: {
  seasonStart: string;
  seasonEnd: string;
  weekDay: number;
  roundNumber: number;
  lastRoundPlayWindowEndLocal?: string;
}): { start: string; end: string } | undefined {
  const inSeason = weeklyPlayWindowForRound({
    seasonStart: input.seasonStart,
    seasonEnd: input.seasonEnd,
    weekDay: input.weekDay,
    roundNumber: input.roundNumber,
  });
  if (inSeason) return inSeason;
  if (!input.lastRoundPlayWindowEndLocal) return undefined;
  return weeklyPlayWindowAfterPreviousRoundEnd(
    input.lastRoundPlayWindowEndLocal,
  );
}

/** Weekday (0=Sun … 6=Sat) of a facility-local yyyy-MM-dd play-window start. */
export function weekDayFromLocalDate(
  localDate: string | undefined,
): number | undefined {
  if (!localDate) return undefined;
  const parsed = parse(localDate.slice(0, 10), ISO, new Date());
  return isValid(parsed) ? getDay(parsed) : undefined;
}

export const WEEKLY_WINDOW_REQUIRED =
  "A play window can't be assigned until this league has a week day.";

/**
 * Weekday a new weekly round opens on. An existing round's window is the
 * schedule the league is already using. With none yet, the season start's
 * weekday is the same default the server uses when no weekday was stored
 * (`LeagueV2SeasonPlanner` omits the weekday and opens on the season start).
 */
export function scheduleWeekDayForNewRound(input: {
  roundWindowStarts: (string | undefined)[];
  seasonStart?: string;
  timezone?: string;
  toLocalDate: (
    value: string | undefined,
    timezone: string | undefined,
    boundary: "start" | "end",
  ) => string | undefined;
}): number | undefined {
  const first = input.roundWindowStarts.find((start) => !!start);
  const fromRound = weekDayFromLocalDate(
    input.toLocalDate(first, input.timezone, "start"),
  );
  if (fromRound != null) return fromRound;
  return weekDayFromLocalDate(input.seasonStart);
}

/** Convert a generated plan round into the form's round model, carrying Flex
 *  semantics faithfully: a Fixed-time round keeps its window, a Flex round keeps
 *  only its due-by (no fabricated window). */
export function planRoundToSeasonItem(
  pr: RoundPlanRound,
  competitionType: Clubhouse_LeagueScoring,
): SeasonRoundItem {
  const window =
    pr.playWindowStartUtc && pr.playWindowEndUtc
      ? {
          playWindowStartUtc: pr.playWindowStartUtc,
          playWindowEndUtc: pr.playWindowEndUtc,
        }
      : { dueByUtc: pr.dueByUtc };
  return {
    roundNumber: pr.roundNumber,
    name: `Round ${pr.roundNumber}`,
    competitionType,
    courseId: pr.courseId || 0,
    settings: withDefaultHoles(pr.settings),
    ...window,
  };
}

function withDefaultHoles(
  settings?: SeasonRoundItem["settings"],
): SeasonRoundItem["settings"] {
  if (settings?.selectedHoles && settings.selectedHoles.length > 0) {
    return settings;
  }
  return { ...settings, selectedHoles: FRONT_9_HOLES };
}

/** Reconcile a freshly generated plan with the operator's saved rounds.
 *  Schedule-owned fields (windows / due-by) always come from the regenerated
 *  plan — so e.g. switching Weekly→Flex drops stale windows — while genuine
 *  per-round overrides (course, conditions) survive by round number. */
export function mergePlanWithEdits(
  planRounds: RoundPlanRound[],
  existing: SeasonRoundItem[],
  competitionType: Clubhouse_LeagueScoring,
): SeasonRoundItem[] {
  const byNumber = new Map(existing.map((r) => [r.roundNumber, r]));
  return planRounds.map((pr) => {
    const base = planRoundToSeasonItem(pr, competitionType);
    const edit = byNumber.get(pr.roundNumber);
    if (!edit) return base;
    return {
      ...base,
      courseId: edit.courseId || base.courseId,
      settings: withDefaultHoles(edit.settings ?? base.settings),
    };
  });
}
